import { useMemo } from 'react'
import type { DocumentRenderData, RequiredField, DocumentType } from './types'
import { TEMPLATE_REGISTRY } from './templates'

export interface DocumentReadiness {
  documentType: DocumentType
  documentName: string
  ready: boolean
  missing: RequiredField[]
}

export function evaluateReadiness(
  documentType: DocumentType,
  data: DocumentRenderData,
): DocumentReadiness {
  const template = TEMPLATE_REGISTRY[documentType]
  const missing: RequiredField[] = []
  for (const field of template.requiredFields) {
    const value = getByDotPath(data, field.key)
    if (field.minArrayLength != null) {
      if (!Array.isArray(value) || value.length < field.minArrayLength) {
        missing.push(field)
      }
      continue
    }
    if (value == null || (typeof value === 'string' && value.trim() === '')) {
      missing.push(field)
    }
  }
  return { documentType, documentName: template.name, ready: missing.length === 0, missing }
}

export function useDocumentReadiness(
  documentType: DocumentType,
  data: DocumentRenderData,
): DocumentReadiness {
  return useMemo(() => evaluateReadiness(documentType, data), [documentType, data])
}

export function evaluateMultiple(
  documentTypes: DocumentType[],
  data: DocumentRenderData,
): { allReady: boolean; perDocument: DocumentReadiness[]; missingCount: number } {
  const perDocument = documentTypes.map(t => evaluateReadiness(t, data))
  const missingCount = perDocument.reduce((sum, r) => sum + r.missing.length, 0)
  return { allReady: perDocument.every(r => r.ready), perDocument, missingCount }
}

function getByDotPath(obj: unknown, path: string): unknown {
  const parts = path.split('.')
  let current: unknown = obj
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined
    current = (current as Record<string, unknown>)[part]
  }
  return current
}
